import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Search, MessageCircle, Trash2, ArrowLeft,
  Users, Hash, Clock, RefreshCw,
} from 'lucide-react';

export default function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [activeConvo,   setActiveConvo]   = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [loadingMsgs,   setLoadingMsgs]   = useState(false);
  const [search,        setSearch]        = useState('');
  const [stats,         setStats]         = useState({ total: 0 });
  const [lastRefresh,   setLastRefresh]   = useState(new Date());
  const bottomRef = useRef(null);

  useEffect(() => { fetchConversations(); }, [search]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const { data } = await api.get('/admin/chat/conversations?' + params.toString());
      setConversations(data.conversations || []);
      setStats({ total: data.total || 0 });
      setLastRefresh(new Date());
    } catch {
      toast.error('Could not load conversations.');
    } finally {
      setLoading(false);
    }
  };

  const openConvo = async (convo) => {
    setActiveConvo(convo);
    setLoadingMsgs(true);
    setMessages([]);

    // Always stringify — MongoDB ObjectId is an object, not a plain string
    const id = String(convo._id);

    try {
      const { data } = await api.get('/admin/chat/messages/' + id);
      setMessages(data.messages || []);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Admin messages fetch failed:', err?.response?.status, err?.response?.data);
      toast.error('Could not load messages.');
    } finally {
      setLoadingMsgs(false);
    }
  };

  const handleDelete = async (rawId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Permanently delete this conversation and all its messages?')) return;
    const id = String(rawId);
    try {
      await api.delete('/admin/chat/conversations/' + id);
      setConversations((prev) => prev.filter((c) => String(c._id) !== id));
      setStats((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
      if (activeConvo && String(activeConvo._id) === id) {
        setActiveConvo(null);
        setMessages([]);
      }
      toast.success('Conversation deleted.');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  const fmt = (d) => new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const fmtRefresh = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // ── Avatar helper ─────────────────────────────────────────
  const Avatar = ({ person, size = 28 }) => (
    person?.avatar
      ? <img src={person.avatar} alt=""
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
      : <div className="avatar-placeholder"
          style={{ width: size, height: size, fontSize: size * 0.35 + 'px', flexShrink: 0,
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {person?.name?.[0] ?? '?'}
        </div>
  );

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

      {/* Header */}
      <div className="admin-page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2>Chat Monitor</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
            Read-only view of all user conversations
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={fetchConversations} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {[
          { icon: Users,         label: 'Total Conversations', value: stats.total,          color: 'var(--info)',    bg: 'var(--info-soft)'    },
          { icon: MessageCircle, label: 'Active in View',      value: conversations.length, color: 'var(--accent)',  bg: 'var(--accent-soft)'  },
          { icon: Hash,          label: 'Messages in Thread',  value: messages.length,      color: 'var(--success)', bg: 'var(--success-soft)' },
          { icon: Clock,         label: 'Last Refresh',        value: fmtRefresh(lastRefresh), color: 'var(--warning)', bg: 'var(--warning-soft)', small: true },
        ].map((s) => (
          <div key={s.label} className="card"
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flex: '1 1 160px', minWidth: 140 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: s.bg, color: s.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <s.icon size={16} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: s.small ? '0.75rem' : '1.1rem', color: 'var(--text)' }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main panel */}
      <div style={{ display: 'flex', gap: 'var(--space-5)', height: 'calc(100vh - 320px)', minHeight: 420 }}>

        {/* Sidebar */}
        <div className="card"
          style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>

          <div style={{ padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div className="admin-search-wrap">
              <Search size={14} className="admin-search-icon" />
              <input className="admin-search" placeholder="Search by name or @username..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="spinner" />
              </div>
            ) : conversations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                No conversations found
              </div>
            ) : conversations.map((convo) => {
              const id       = String(convo._id);
              const activeId = activeConvo ? String(activeConvo._id) : null;
              const isActive = id === activeId;
              const [p1, p2] = convo.participants || [];

              return (
                <div key={id} onClick={() => openConvo(convo)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                    background: isActive ? 'var(--accent-soft)' : 'transparent',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}>

                  {/* Overlapping avatars */}
                  <div style={{ position: 'relative', width: 46, height: 36, flexShrink: 0 }}>
                    {[p1, p2].map((p, i) => (
                      p?.avatar
                        ? <img key={i} src={p.avatar} alt="" style={{
                              width: 26, height: 26, borderRadius: '50%', objectFit: 'cover',
                              position: 'absolute', top: i * 8, left: i * 12,
                              border: '2px solid var(--surface)' }} />
                        : <div key={i} className="avatar-placeholder" style={{
                              width: 26, height: 26, fontSize: '0.6rem',
                              position: 'absolute', top: i * 8, left: i * 12,
                              border: '2px solid var(--surface)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {p?.name?.[0] ?? '?'}
                          </div>
                    ))}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p1?.name} &amp; {p2?.name}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--accent)' }}>
                      @{p1?.username} · @{p2?.username}
                    </div>
                    {convo.lastMessage?.text && (
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {convo.lastMessage.text}
                      </div>
                    )}
                  </div>

                  <button className="btn btn-ghost btn-icon"
                    style={{ color: 'var(--danger)', flexShrink: 0 }}
                    onClick={(e) => handleDelete(id, e)} title="Delete">
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Messages panel */}
        <div className="card"
          style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>

          {!activeConvo ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                          justifyContent: 'center', gap: 12, color: 'var(--text-light)', textAlign: 'center', padding: 40 }}>
              <MessageCircle size={40} strokeWidth={1.5} />
              <p style={{ fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>Select a conversation</p>
              <span style={{ fontSize: '0.82rem' }}>Click any conversation to read messages</span>
            </div>
          ) : (
            <>
              {/* Convo header */}
              {(() => {
                const [p1, p2] = activeConvo.participants || [];
                return (
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)',
                                display: 'flex', alignItems: 'center', gap: 12,
                                background: 'var(--surface)', flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-icon"
                      onClick={() => { setActiveConvo(null); setMessages([]); }}>
                      <ArrowLeft size={16} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar person={p1} size={30} />
                      <Avatar person={p2} size={30} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>
                        {p1?.name} &amp; {p2?.name}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--accent)' }}>
                        @{p1?.username} · @{p2?.username}
                        {messages.length > 0 && (
                          <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                            · {messages.length} message{messages.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <button className="btn btn-danger btn-sm"
                      onClick={(e) => handleDelete(activeConvo._id, e)}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                );
              })()}

              {/* Thread */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px',
                            display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--bg)' }}>
                {loadingMsgs ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <div className="spinner" />
                  </div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, fontSize: '0.875rem' }}>
                    No messages in this conversation
                  </div>
                ) : messages.map((msg, idx) => {
                  const showDate = idx === 0 ||
                    new Date(msg.createdAt).toDateString() !== new Date(messages[idx - 1]?.createdAt).toDateString();

                  return (
                    <div key={String(msg._id)}>
                      {showDate && (
                        <div style={{ textAlign: 'center', margin: '14px 0 10px' }}>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-light)',
                                         background: 'var(--bg-2)', border: '1px solid var(--border)',
                                         borderRadius: 999, padding: '3px 14px' }}>
                            {fmtDate(msg.createdAt)}
                          </span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                        <Avatar person={msg.sender} size={28} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.68rem', marginBottom: 4,
                                        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
                              @{msg.sender?.username}
                            </span>
                            <span style={{ color: 'var(--text-light)' }}>{msg.sender?.name}</span>
                            <span style={{ color: 'var(--text-light)' }}>· {fmt(msg.createdAt)}</span>
                          </div>
                          <div style={{ display: 'inline-block', padding: '9px 14px',
                                        background: 'var(--surface)', border: '1px solid var(--border)',
                                        borderRadius: 16, borderBottomLeftRadius: 4,
                                        fontSize: '0.875rem', lineHeight: 1.5,
                                        maxWidth: 480, wordBreak: 'break-word', color: 'var(--text)' }}>
                            {msg.text}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}